// IdP (@giftme/auth) login for the admin: passwordless SMS OTP → admin-audience access token + a
// rotating refresh token (sliding session). Ported from the baseline admin; the only auth module here.
import { clearStepUp } from "./auth/step-up-controller.js";

const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE as string | undefined) ?? "http://localhost:8303/api";

const TOKEN_KEY = "giftme2.token";
const REFRESH_KEY = "giftme2.refresh";
let token = localStorage.getItem(TOKEN_KEY) ?? "";
let refreshTok = localStorage.getItem(REFRESH_KEY) ?? "";

// Every tab of the panel shares ONE session, and localStorage is its record. A refresh token is single-use:
// the IdP rotates it on every refresh and takes a second use as theft — it revokes the whole session. A tab
// that kept the copy it read at load, and refreshed with it after another tab had rotated it, signed every
// tab out with a false "refresh REUSE detected". So the tabs follow storage, a refresh always uses the token
// in storage, and one tab at a time refreshes (tryRefresh).
window.addEventListener("storage", (e) => {
  if (e.key === TOKEN_KEY || e.key === null) token = localStorage.getItem(TOKEN_KEY) ?? "";
  if (e.key === REFRESH_KEY || e.key === null) refreshTok = localStorage.getItem(REFRESH_KEY) ?? "";
});

function jwtPayload(t: string): Record<string, unknown> {
  try {
    const seg = t.split(".")[1] ?? "";
    const b = seg.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b + "=".repeat((4 - (b.length % 4)) % 4)));
  } catch { return {}; }
}

export const auth = {
  token: () => token,
  isAuthed: () => !!token,
  scopes: (): Set<string> => new Set(String(jwtPayload(token).scope ?? "").split(/\s+/).filter(Boolean)),
  set: (t: string, r?: string | null) => {
    token = t; localStorage.setItem(TOKEN_KEY, t);
    if (r !== undefined) { refreshTok = r ?? ""; r ? localStorage.setItem(REFRESH_KEY, r) : localStorage.removeItem(REFRESH_KEY); }
  },
  // Also drop any cached ELEVATED (step-up) tokens — they must never outlive the session that earned them.
  clear: () => { token = ""; refreshTok = ""; localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(REFRESH_KEY); clearStepUp(); },
};

// Sliding session: rotate the access token via the refresh token (rotation + reuse-detection server-side).
// Single-flight within the tab (refreshing) and across tabs (a Web Lock): two tabs refreshing at once would
// send the same token twice — a reuse to the IdP.
let refreshing: Promise<boolean> | null = null;
const acrossTabs = <T,>(fn: () => Promise<T>): Promise<T> =>
  // lib.dom types the callback's promise as the lock's value (Promise<Promise<T>>); a lock resolves with it awaited.
  typeof navigator !== "undefined" && navigator.locks ? (navigator.locks.request("giftme2.refresh", fn) as unknown as Promise<T>) : fn();
const expiresSoon = (t: string) => Number(jwtPayload(t).exp ?? 0) - SKEW_SEC <= Math.floor(Date.now() / 1000);
export async function tryRefresh(): Promise<boolean> {
  if (!refreshing) refreshing = acrossTabs(async () => {
    // Storage, not this tab's memory: another tab may have rotated while this one slept or waited for the lock.
    const stored = localStorage.getItem(TOKEN_KEY) ?? "";
    refreshTok = localStorage.getItem(REFRESH_KEY) ?? "";
    if (!refreshTok) { token = stored; return false; }                            // signed out in another tab
    if (stored && stored !== token && !expiresSoon(stored)) { token = stored; return true; }  // it already refreshed
    try {
      const res = await fetch(`${AUTH_BASE}/oauth/token`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grant_type: "refresh_token", client_id: "giftme-admin", refresh_token: refreshTok }),
      });
      const d = res.ok ? await res.json() : null;
      if (!d?.access_token) return false;
      auth.set(d.access_token, d.refresh_token ?? null);
      return true;
    } catch { return false; }
  }).finally(() => { refreshing = null; });
  return refreshing;
}

// Proactive refresh: if the stored access token is expired (or within SKEW of it) — the usual state
// on a fresh page load after the tab sat idle — rotate BEFORE firing requests, so the first wave
// carries a fresh token instead of each call 401ing and retrying. Single-flight via tryRefresh().
const SKEW_SEC = 30;
export async function ensureFresh(): Promise<void> {
  if (!token) return;
  const exp = Number(jwtPayload(token).exp ?? 0);
  if (exp && exp - SKEW_SEC > Math.floor(Date.now() / 1000)) return; // still valid
  await tryRefresh();
}

/** Test the IdP SMS pipeline by sending a real passwordless OTP to a phone (app realm, auto-provisions). */
export async function sendTestSms(phone: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${AUTH_BASE}/passwordless/start`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone_number: phone, realm: "app", client_id: "giftme-app" }),
    });
    if (r.ok) return { ok: true };
    const d = await r.json().catch(() => ({} as Record<string, string>));
    return { ok: false, error: d.error_description || d.error || `HTTP ${r.status}` };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

/** The signed-in user's profile (name/email) from the IdP — for the "Hello, {name}" header. */
export async function getUserInfo(): Promise<{ name?: string; email?: string }> {
  try {
    await ensureFresh(); // refresh a stale token first, else this 401s and the header falls back to "Admin"
    const r = await fetch(`${AUTH_BASE}/userinfo`, { headers: { Authorization: `Bearer ${token}` } });
    return r.ok ? await r.json() : {};
  } catch { return {}; }
}

// ── Step-up: re-authorize a SENSITIVE action with a fresh OTP. The IdP drops sensitive scopes
// (e.g. manage:giftme:identities) from a normal login, so they're obtained only here. ──
/** Step-up 1 — send a fresh OTP to the signed-in admin's OWN phone. */
export async function stepUpStart(): Promise<{ phone_hint?: string }> {
  const r = await fetch(`${AUTH_BASE}/step-up/start`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error("Could not send the code");
  return r.json();
}
/** Step-up 2 — exchange the OTP for a SHORT-LIVED token carrying `scope` (does NOT replace the session).
 *  Returns the token AND its lifetime: the IdP's `expires_in` is the authoritative per-scope TTL
 *  (scopes.step_up_ttl, default 120s), so callers can reuse the elevated token until it expires
 *  instead of burning a new OTP per action. */
export async function stepUpWithTtl(otp: string, scope: string): Promise<{ token: string; expiresIn: number }> {
  const r = await fetch(`${AUTH_BASE}/oauth/token`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grant_type: "urn:giftme:params:oauth:grant-type:step-up", client_id: "giftme-admin", subject_token: token, otp, scope, audience: "https://admin.giftme/" }),
  });
  const d = await r.json().catch(() => ({} as Record<string, string>));
  if (!r.ok || !d.access_token) throw new Error(d.error_description || d.error || "Invalid or expired code");
  // expires_in is seconds (OAuth). Missing/!numeric ⇒ 0 ⇒ the caller treats it as non-cacheable.
  const expiresIn = Number(d.expires_in);
  return { token: d.access_token as string, expiresIn: Number.isFinite(expiresIn) ? expiresIn : 0 };
}

/** Step-up 2 (token only) — for callers that don't reuse the elevated token. */
export async function stepUp(otp: string, scope: string): Promise<string> {
  return (await stepUpWithTtl(otp, scope)).token;
}

/** Step 1 — send the SMS code to a staff phone. */
export async function startLogin(phone: string): Promise<void> {
  const res = await fetch(`${AUTH_BASE}/passwordless/start`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone_number: phone, realm: "staff", client_id: "giftme-admin" }),
  });
  if (!res.ok) throw new Error("Could not send the code");
}

/** Step 2 — exchange the code for an admin access token (audience=admin) and store it. */
export async function verifyLogin(phone: string, code: string): Promise<void> {
  const res = await fetch(`${AUTH_BASE}/oauth/token`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "http://auth0.com/oauth/grant-type/passwordless/otp",
      client_id: "giftme-admin", realm: "staff", username: phone, otp: code,
      audience: "https://admin.giftme/",
      scope: "openid profile email offline_access manage:giftme:catalog manage:giftme:brands manage:giftme:providers manage:giftme:identities read:giftme:cards read:giftme:discountProviders read:giftme:support write:giftme:support send:sms",
    }),
  });
  const d = await res.json().catch(() => ({} as Record<string, string>));
  if (!res.ok || !d.access_token) throw new Error(d.error_description || d.error || "Invalid or expired code");
  auth.set(d.access_token, d.refresh_token ?? null);
}
